import { useState, useEffect } from "react";
import {
    DataGrid,
    GridColDef,
    GridPaginationModel,
    GridRowId,
    GridRowModesModel,
    GridRowParams,
    GridSortModel,
    GridToolbarColumnsButton,
    GridToolbarContainer,
    GridToolbarDensitySelector,
    GridToolbarQuickFilter,
} from "@mui/x-data-grid";
import api from "../../../services/ApiService";
import { ApiResponse } from "../../../models/ApiResponse";
import {
    SelectedFilters,
} from "../../../models/Items/ItemListRequest";
import { SortDirection } from "../../../models/Enum";
import {
    Box,
    Button,
    Checkbox,
    FormControlLabel,
    Tooltip,
} from "@mui/material";
import Loader from "../../../components/Loader";
import { NO_LOCAL_SEARCH_RESULTS_GRID_MESSAGE, NO_ROWS_GRID_MESSAGE } from "../../../components/GridUtilityComponents";
import { useAppDispatch, useAppSelector } from "../../../store";
import moment from "moment";
import { DATE_TIME_FORMAT } from "../../../utils/constants";
import Filters from "../../Products/Filters";
import { NotificationLogDetail, VIEW_NOTIFICATION_LOGS } from "../../Products/EditItem/Constants";
import { initialNotificationLogRequest, updateIsUserCleared, updateNotificationLogRequest } from "../../../reducers/notificationLogsReducer";
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import SessionService from "../../../services/SessionService";
import { toast } from "react-toastify";
import ConfirmationModal from "../../../components/ConfirmationModal";


export interface NotificationLogsResponse {
    notifications?: NotificationLogDetail[];
    totalCount?: number;
}

export default function NotificationLogs() {
    const [loading, setLoading] = useState(false);
    const [notificationLogs, setNotificationLogs] = useState<NotificationLogDetail[]>([]);

    const [filtersExpanded, setFiltersExpanded] = useState<boolean>(true);
    const [logsTotalCount, setLogsTotalCount] = useState(0);
    const [rowModesModel, setRowModesModel] = useState<GridRowModesModel>({});
    const dispatch = useAppDispatch();
    const [selectedRows, setSelectedRows] = useState([]);
    const [allRecordsSelected, setAllRecordsSelected] = useState(false);
    const [markAsReadConfirmationModal, setMarkAsReadConfirmationModal] = useState(false);
    const notificationLogsRequestState = useAppSelector((state) => state.notificationLogs.notificationLogRequest);
    const notificationLogsViewSelectedFilterValues = useAppSelector((state) => state?.notificationLogs?.notificationLogFilter?.selectedFilterValues);
    const notificationLogsReferenceColumnsMapping = {
        stockCode: "stockCode",
        baseCode: "baseCode",
        seriesName: "seriesName",
        brandCollection: "brandCollection",
        marketingDesigner: "marketingDesigner",
        notification: "notification",
        notificationDate: "notificationDate",
    };
    const sessionService = SessionService.getInstance();

    const renderGridHeader = (headerName, field) => (
        <div className="column-header">
            <span>{headerName}</span>
            {notificationLogsRequestState.sortBy === field && <span className="sort-arrow">
                {notificationLogsRequestState.sortDirection === SortDirection.ASC ? <ArrowUpwardIcon /> : <ArrowDownwardIcon />}
            </span>}
        </div>
    )

    const notificationLogsGridColumns: GridColDef[] = [
        {
            field: "stockCode",
            headerName: "Stock Code",
            width: 200,
            disableColumnMenu: true,
            sortable: true,
            renderCell: (params) => {
                return params?.row?.stockcode ? <Box
                    component="span"
                    className="link-cell stockcode-cell"
                    onClick={() => {
                        window.open(`/edit-product/${params.row.productId}`, "_blank", "noopener,noreferrer");
                    }}
                    title={params?.row?.stockcode}
                >
                    {params?.row?.stockcode}
                </Box> : ""
            },
            renderHeader: () => renderGridHeader("Stock Code", "stockCode"),
            hideSortIcons: true,
        },
        {
            field: "baseCode",
            headerName: "Base Code",
            sortable: true,
            disableColumnMenu: true,
            width: 200,
            renderCell: (params) => {
                return params?.row?.baseCode ? <Box
                    component="span"
                    className="link-cell stockcode-cell"
                    onClick={() => {
                        window.open(`/edit-product/${params.row.productId}`, "_blank", "noopener,noreferrer");
                    }}
                    title={params?.row?.baseCode}
                >
                    {params?.row?.baseCode}
                </Box> : ""
            },
            renderHeader: () => renderGridHeader("Base Code", "baseCode"),
            hideSortIcons: true,
        },
        {
            field: "brandCollection",
            headerName: "Brand Collection",
            sortable: true,
            disableColumnMenu: true,
            width: 200,
            renderHeader: () => renderGridHeader("Brand Collection", "brandCollection"),
            hideSortIcons: true,
        },
        {
            field: "seriesName",
            headerName: "Series Name",
            sortable: true,
            disableColumnMenu: true,
            width: 150,
            renderHeader: () => renderGridHeader("Series Name", "seriesName"),
            hideSortIcons: true,
        },
        {
            field: "marketingDesigner",
            headerName: "Designer",
            sortable: true,
            disableColumnMenu: true,
            width: 150,
            renderHeader: () => renderGridHeader("Designer", "marketingDesigner"),
            hideSortIcons: true,
        },
        {
            field: "developmentType",
            headerName: "Development Type",
            sortable: false,
            disableColumnMenu: true,
            width: 150,
            renderHeader: () => renderGridHeader("Development Type", "developmentType"),
            hideSortIcons: true,
        },
        {
            field: "notification",
            headerName: "Notification",
            sortable: true,
            disableColumnMenu: true,
            width: 600,
            renderCell: (params) => {
                return params?.row?.notification ? <Box
                    component="span"
                    className="link-cell notification-cell"
                    dangerouslySetInnerHTML={{ __html: params?.row?.notification }}
                    title={params?.row?.notification?.replace(/<[^>]*>/g, '')}
                /> : ""
            },
            renderHeader: () => renderGridHeader("Notification", "notification"),
            hideSortIcons: true,
        },
        {
            field: "notificationDate",
            headerName: "Notification Date",
            sortable: true,
            disableColumnMenu: true,
            width: 200,
            valueFormatter: (params) => {
                return params?.value ? moment.utc(params?.value).local().format(DATE_TIME_FORMAT) : "";
            },
            renderHeader: () => renderGridHeader("Notification Date", "notificationDate"),
            hideSortIcons: true,
        },
    ];

    useEffect(() => {
        if(notificationLogsViewSelectedFilterValues?.userId !== sessionService.getCurrentUser()?.id) {
            setSelectedRows([]);
            setAllRecordsSelected(false);
        }
    }, [notificationLogsViewSelectedFilterValues]);

    const gridToolbar = () => (
        <GridToolbarContainer sx={{justifyContent: "space-between"}}>
            <div style={{ marginLeft: "12px" }}>
                {<Tooltip title={
                        (notificationLogsViewSelectedFilterValues?.userId !== sessionService.getCurrentUser()?.id 
                          || notificationLogsRequestState?.userId !== sessionService.getCurrentUser()?.id)
                            ? "Users can only select their own notification logs. To find them, filter the list by your name." 
                            : logsTotalCount > 1000 
                                ? "Cannot select more than 1000 records" 
                                : ""}>
                    <FormControlLabel
                        className="notification-select-all-checkbox-label"
                        sx = {{'& .MuiTypography-root': { fontSize: '13px', fontWeight: 500 }}}          
                        control={
                        <Checkbox
                            checked={allRecordsSelected}
                            disabled={(logsTotalCount === 0) || (logsTotalCount > 1000) ||
                                (notificationLogsViewSelectedFilterValues?.userId !== sessionService.getCurrentUser()?.id 
                                || notificationLogsRequestState?.userId !== sessionService.getCurrentUser()?.id
                            )}
                            onChange={handleSelectAll}
                        />
                        } 
                        label={`SELECT ALL(${logsTotalCount})`}          
                    />
                    </Tooltip>}
                <GridToolbarColumnsButton />
                <GridToolbarDensitySelector />
                <Button
                    className="notification-mark-as-read-button"
                    type="button"
                    variant="contained"
                    disabled={(notificationLogsViewSelectedFilterValues?.userId !== sessionService.getCurrentUser()?.id 
                        || notificationLogsRequestState?.userId !== sessionService.getCurrentUser()?.id)  || selectedRows?.length === 0}
                    onClick={() => setMarkAsReadConfirmationModal(true)}
                >
                    Mark As Read
                </Button>
            </div>
            <GridToolbarQuickFilter sx={{ margin: "5px 30px 5px 5px", alignSelf: "flex-end", width: "20%" }} />
        </GridToolbarContainer>
    );

    useEffect(() => {
        getNotificationLogs();
    }, [notificationLogsRequestState]);

    const getNotificationLogs = async () => {
        setLoading(true);

        api.post<ApiResponse<NotificationLogsResponse>>("/Notification/log", notificationLogsRequestState)
            .then((response) => {
                if (response.isSuccess) {
                    setLogsTotalCount(response?.data?.totalCount || 0);
                    setNotificationLogs(response?.data?.notifications || []);
                    if(allRecordsSelected) {
                        const itemsForSelection = response?.data?.notifications?.map(i => { 
                            return {
                            id: i.id,
                            } 
                        });
                        setSelectedRows(selectedRows.concat(itemsForSelection.filter(ii => !selectedRows.some(sr => sr.id === ii.id))));
                        }
                } else {
                    throw new Error(response.message);
                }
                setLoading(false);
            })
            .catch((error) => {
                setLoading(false);
                console.error("Exception from Notification logs API", error);
            });
    };

    const onPaginationChange = (pagination: GridPaginationModel) => {
        dispatch(updateNotificationLogRequest({
            ...notificationLogsRequestState,
            pageIndex: pagination.page + 1,
            pageSize: pagination.pageSize,
        }));
    };

    const handleApplyFilterClick = (filterValues: SelectedFilters) => {
        if (Object.values(filterValues)?.length) {
            dispatch(updateNotificationLogRequest({
                pageSize: notificationLogsRequestState.pageSize,
                sortBy: notificationLogsRequestState.sortBy,
                sortDirection: notificationLogsRequestState.sortDirection,
                ...filterValues,
                pageIndex: 1,
            }));
        } else {
            //reset the filters
            dispatch(updateNotificationLogRequest({
                ...initialNotificationLogRequest,
                userId: sessionService.getCurrentUser()?.id,
            }));
            dispatch(updateIsUserCleared(false));
        }
        setAllRecordsSelected(false);
        setSelectedRows([]);
    };

    const handleExpandClick = () => {
        setFiltersExpanded(!filtersExpanded);
    };

    const getSortFieldName = (gridFieldName: string): string => {
        return notificationLogsReferenceColumnsMapping[gridFieldName] || gridFieldName;
    };

    const onSortChange = (sorting: GridSortModel) => {
        if (sorting?.length === 0) {
            dispatch(updateNotificationLogRequest({
                ...notificationLogsRequestState,
                sortBy: notificationLogsRequestState?.sortBy || initialNotificationLogRequest.sortBy,
                sortDirection: SortDirection.ASC
                
            }));
        } else {
            dispatch(updateNotificationLogRequest({
                ...notificationLogsRequestState,
                sortBy: getSortFieldName(sorting[0].field),
                sortDirection:
                    (sorting[0].field !== notificationLogsRequestState?.sortBy)
                        ? SortDirection.ASC
                        : (notificationLogsRequestState?.sortDirection === SortDirection.DESC ? SortDirection.ASC : SortDirection.DESC)
            }));
        }
    };

    const handleRowModesModelChange = (newRowModesModel: GridRowModesModel) => {
        setRowModesModel(newRowModesModel);
    };

    const handleSelectAll = () => {
        const allRecordsSelectedNewValue = !allRecordsSelected;
        setAllRecordsSelected(allRecordsSelectedNewValue);
        // push current page's records into the selectedRows state so that grid shows them selected.
        // Other pages will be pushed to selectedRows as and when user nagivates through pages
        if(allRecordsSelectedNewValue === true) {
        const itemIDs = notificationLogs.map(i => { 
            return {
            id: i.id,
            } 
        });
        setSelectedRows(selectedRows.concat(itemIDs.filter(ii => !selectedRows.some(sr => sr.id === ii.id))));
        }
        else {
        setSelectedRows([]);
        }
    };

    const onRowSelectionModelChange = (rowIDs: GridRowId[]) => {
        // update the selectedRows state only if allRecordsSelected is false.
        if(!allRecordsSelected) {
          // NOTE: rowIDs contains IDs of all rows that were previously selected + new one that trigged this event
          //get stockCode and baseCode for the selected rows and store in state
          const selectedItems = notificationLogs.filter(i => rowIDs.includes(i.id));  
          const newSelection = selectedItems.map(i => {
            return {
              id: i.id,
              baseCode: i.baseCode,
            }
            //then filter the duplicate records for new selection.
          }).filter(newlySelectedItem => !selectedRows.some(sr => sr.id === newlySelectedItem.id));
          
          // if product was unchecked, remove it from previously selectedRows.
          const existingSelection = selectedRows.filter(sr => rowIDs.includes(sr.id));
          
          //set previous selection and add new selection.
          setSelectedRows([...existingSelection, ...newSelection]);
        }
      };

      const getAllNotificationIds = async () => {
        try {
            const response = await api.post<ApiResponse<number[]>>("/Notification/log-ids", notificationLogsViewSelectedFilterValues);
            return response.data;
        } catch (error) {
            console.error("Exception from getting notification ids API", error);
            return [];
        }
      }

      const handleMarkAsRead = async () => {
        setLoading(true);
        let selectedNotificationIds = selectedRows.map(sr => sr.id);

        if(allRecordsSelected && logsTotalCount > notificationLogsRequestState?.pageSize) {
            selectedNotificationIds = await getAllNotificationIds();
        }

        if(selectedNotificationIds.length === 0) {
             return setLoading(false);
        }
        api.put<ApiResponse<NotificationLogsResponse>>("/Notification/log-read", 
            selectedNotificationIds)
            .then(async (response) => {
                if (response.isSuccess) {
                    toast.success(response?.message || "Notification marked as read successfully");
                    setSelectedRows([]);
                    setAllRecordsSelected(false);
                    await getNotificationLogs();
                } else {
                    toast.error(response?.message || "Failed to mark notification as read");
                }
            })
            .catch((error) => {
                toast.error("Failed to mark notification as read");
                console.error("Exception from Notification logs API", error);
            }).finally(() => {
                setMarkAsReadConfirmationModal(false);
                setLoading(false);
            });
      }

      const handleCloseMarkAsReadConfirmationModal = () => {
        setMarkAsReadConfirmationModal(false);
      };

    return (
        <>
            <ConfirmationModal
                isOpen={markAsReadConfirmationModal}
                onClose={handleCloseMarkAsReadConfirmationModal}
                title={"Confirm Mark As Read Notification(s)"}
                message={"Are you sure you want to mark selected notification(s) as read?"}
                handleConfirmYes={handleMarkAsRead}
                handleConfirmNo={handleCloseMarkAsReadConfirmationModal}
                />
            <Filters
                onApplyFilter={handleApplyFilterClick}
                filtersExpanded={filtersExpanded}
                handleExpandClick={handleExpandClick}
                viewMode={VIEW_NOTIFICATION_LOGS}
            />
            <Loader isLoading={loading} />
            <Box
                className="data-grid-box notification-logs-grid-box"
            >
                <DataGrid
                    className="data-grid notification-logs-grid"
                    rows={notificationLogs}
                    columns={notificationLogsGridColumns}
                    initialState={{
                        pagination: {
                            paginationModel: {
                                pageSize: 10
                            },
                        },
                    }}
                    rowCount={logsTotalCount}
                    pageSizeOptions={[10, 25, 50, 100]}
                    disableRowSelectionOnClick={true}
                    disableColumnFilter={true}
                    sortingMode="server"
                    paginationMode="server"
                    // sortModel={sortModel}
                    onSortModelChange={(model) => onSortChange(model)}
                    onPaginationModelChange={onPaginationChange}
                    paginationModel={{
                        page: notificationLogsRequestState.pageIndex - 1,
                        pageSize: notificationLogsRequestState.pageSize,
                    }}
                    slots={{
                        toolbar: gridToolbar,
                        noRowsOverlay: () => NO_ROWS_GRID_MESSAGE,
                        noResultsOverlay: () => NO_LOCAL_SEARCH_RESULTS_GRID_MESSAGE,
                    }}
                    getRowHeight={() => 'auto'}
                    rowModesModel={rowModesModel}
                    onRowModesModelChange={handleRowModesModelChange}
                    checkboxSelection={true}
                    isRowSelectable={(params: GridRowParams) => {
                        return (notificationLogsViewSelectedFilterValues?.userId === sessionService.getCurrentUser()?.id 
                     && notificationLogsRequestState?.userId === sessionService.getCurrentUser()?.id)
                     && (!allRecordsSelected) 
                    }}
                    onRowSelectionModelChange={onRowSelectionModelChange}
                    rowSelectionModel={(notificationLogsViewSelectedFilterValues?.userId === sessionService.getCurrentUser()?.id 
                        && notificationLogsRequestState?.userId === sessionService.getCurrentUser()?.id) 
                        && selectedRows?.length > 0 ? selectedRows?.map(sr => sr.id) : []}
                    keepNonExistentRowsSelected
                    hideFooterSelectedRowCount={allRecordsSelected}
                />
            </Box>
        </>
    );
}